import cn from 'clsx'
import React, { useCallback } from 'react'
import { DropTarget } from 'react-dnd'
import { useDispatch } from 'hooks/use-redux'
import type { ExplorerPane as TExplorerPane } from 'types/explorer'
import type { NestingLabel, Track } from 'types/track'
import {
  loadPane,
  updatePane,
} from '@disco/state-management/actions/ExplorerActions'
import { TRACK_NESTING_DND } from '@disco/constants/FeatureTypes'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { FILE, TRACK } from '@disco/constants/ItemTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getFilesFromDraggedItem,
  getFileTypeByExt,
} from '@disco/utils/file-utils'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { DropFilesIndicator } from 'components/ui/drop-indicator'
import { isReadOnlyTrack } from '@disco/utils/tracks'
import useTimeout from 'hooks/use-timeout'
const DRAGHOLD_DELAY = 1000
const trackTarget = {
  canDrop(props, monitor) {
    const item = monitor.getItem()

    if (item.track) {
      if (isReadOnlyTrack(item.track)) return false
      if (isReadOnlyTrack(props.track)) return false

      if (props.track && props.track.id === item.track.id) {
        return false
      }

      const type = getFileTypeByExt(item.track.ext)
      // we allow dropping only audio files for nesting
      return type === AUDIO_FILE
    }

    if (item.files) return true
    return false
  },

  drop(props, monitor) {
    const dropResult = monitor.getDropResult()
    const draggedItem = monitor.getItem()
    if (dropResult?.alreadyHandled) return props

    if (props.onDrop) {
      // getDropResult contains index
      return props.onDrop(dropResult, draggedItem)
    }

    if (draggedItem.items && props.uploadFilesToMainTrack) {
      getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
        if (files.length > 0) {
          props.uploadFilesToMainTrack(
            props.track,
            files,
            props.nestingLabel,
            dropResult?.index,
          )
        }
      })
    }

    if (
      draggedItem.track &&
      getFileTypeByExt(draggedItem.track.ext) === AUDIO_FILE &&
      props.addNestedTracksToMain
    ) {
      const trackSelected =
        props.selectedItems.length > 0 &&
        isDraggedItemPartOfSelection(props.selectedItems, draggedItem)

      if (!trackSelected) {
        props.addNestedTracksToMain(
          props.track,
          [draggedItem.track],
          props.nestingLabel,
          dropResult?.index,
        )
      } else {
        const tracks = props.selectedItems.filter(
          item =>
            item.type === TRACK && getFileTypeByExt(item.ext) === AUDIO_FILE,
        )

        if (tracks.length > 0) {
          props.addNestedTracksToMain(
            props.track,
            tracks,
            props.nestingLabel,
            dropResult?.index,
          )
        }
      }
    }

    return { ...props, alreadyHandled: true }
  },
}

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver:
      monitor.canDrop() &&
      monitor.isOver({
        shallow: true,
      }),
    draggedItem: monitor.getItem(),
  }
}

type Props = {
  children: React.ReactNode
  connectDropTarget: (...args: Array<any>) => any
  index?: number
  itemPrefix?: string
  draggedItem: Record<string, any> | null | undefined
  isOver: boolean
  nestingLabel?: NestingLabel
  pane?: TExplorerPane
  track: Track
  onDragHold?: () => void
  onDrop?: (dropResult: Record<string, any>) => void
  // TODO: remove when all businesses have TRACK_NESTING_DND
  location?: 'manage-nesting'
  disabled?: boolean
}

const TrackDropWrapper = ({
  children,
  draggedItem,
  disabled,
  connectDropTarget,
  index,
  isOver,
  itemPrefix = 'pane',
  nestingLabel,
  pane,
  track,
  onDragHold,
  onDrop,
  // TODO: remove when all businesses have TRACK_NESTING_DND
  location,
}: Props) => {
  const dispatch = useDispatch()
  const nestingDndEnabled =
    useFeatureEnabled(TRACK_NESTING_DND) || location === 'manage-nesting'
  const handleDragHold = useCallback(() => {
    if (pane && typeof index === 'number') {
      dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${index}` }))
      dispatch(
        loadPane({
          index: pane?.index + 1,
          title: track.name,
          type: 'track',
          url: track.url,
        }),
      )
    }

    if (onDragHold) {
      onDragHold()
    }
  }, [dispatch, index, pane, track, onDragHold, itemPrefix])
  useTimeout(
    handleDragHold,
    isOver && track && nestingDndEnabled ? DRAGHOLD_DELAY : null,
  )
  const dropTargetClass = cn({
    HighlightedDropTarget: isOver,
    'HighlightedDropTarget--blink': isOver && track,
  })
  if (!nestingDndEnabled || disabled) return children
  return connectDropTarget(
    <div className={dropTargetClass}>
      {children}
      {isOver && draggedItem?.files && <DropFilesIndicator />}
    </div>,
  )
}

export default DropTarget(
  [FILE, TRACK],
  trackTarget,
  collect,
)(TrackDropWrapper) as any
